/**
 * IndustryConfigScreen — Industry-specific Item Configuration
 * Uses: AdvancedFormBuilder for form design, AdvancedGeneratedForm for rendering
 * Location: Central Hub → System Manager → Control Management (21.1.8)
 * 
 * Pre-built templates for: Retail, Fashion/Apparel, Grocery, Electronics, Pharmacy
 * Admin designs field configs per industry → saved via /api/formbuilder
 * Product Master loads the active config to show/hide fields dynamically
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Factory, ShoppingBag, Shirt, Apple, Monitor, Pill, Plus, Check, Trash2, Edit3, Eye, ChevronLeft, Copy } from 'lucide-react';
import AdvancedFormBuilder from '../../components/formbuilder/AdvancedFormBuilder';
import AdvancedGeneratedForm from '../../components/formbuilder/AdvancedGeneratedForm';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';


/* ═══════════════════════════════════════════
   INDUSTRY TEMPLATES — pre-built field configs
═══════════════════════════════════════════ */
const INDUSTRY_TEMPLATES = [
  {
    id: 'retail_general',
    name: 'Retail — General',
    icon: ShoppingBag,
    color: '#2563eb',
    description: 'Standard retail: Division, Dept, Category, Brand, MRP, WSP, Tax',
    sections: [
      { id: 'section-1', title: 'Classification', elements: [
        { id: 'el-div', type: 'text', label: 'Division', required: false, placeholder: 'Select Division' },
        { id: 'el-dept', type: 'text', label: 'Department', required: false, placeholder: 'Select Department' },
        { id: 'el-cat', type: 'text', label: 'Category', required: true, placeholder: 'Select Category' },
        { id: 'el-brand', type: 'text', label: 'Brand', required: false, placeholder: 'Select Brand' },
      ]},
      { id: 'section-2', title: 'Pricing', elements: [
        { id: 'el-mrp', type: 'number', label: 'MRP', required: true },
        { id: 'el-rate', type: 'number', label: 'Rate (Cost)', required: true },
        { id: 'el-wsp', type: 'number', label: 'WSP', required: false },
        { id: 'el-disc', type: 'number', label: 'Discount %', required: false },
      ]},
      { id: 'section-3', title: 'Tax & UOM', elements: [
        { id: 'el-itax', type: 'text', label: 'Input Tax', required: false },
        { id: 'el-otax', type: 'text', label: 'Output Tax', required: false },
        { id: 'el-uom', type: 'text', label: 'Base Unit', required: false },
        { id: 'el-hsn', type: 'text', label: 'HSN Code', required: false },
      ]},
    ],
  },
  {
    id: 'fashion_apparel',
    name: 'Fashion & Apparel',
    icon: Shirt,
    color: '#7c3aed',
    description: 'Fashion: Size, Color, Style, Fit, Season, Fabric, Pattern + standard fields',
    sections: [
      { id: 'section-1', title: 'Classification', elements: [
        { id: 'el-div', type: 'text', label: 'Division', required: true },
        { id: 'el-dept', type: 'text', label: 'Department', required: true },
        { id: 'el-cat', type: 'text', label: 'Category', required: true },
        { id: 'el-brand', type: 'text', label: 'Brand', required: true },
      ]},
      { id: 'section-2', title: 'Attributes', elements: [
        { id: 'el-size', type: 'select', label: 'Size', required: true, options: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Free Size'] },
        { id: 'el-color', type: 'text', label: 'Color', required: true },
        { id: 'el-style', type: 'text', label: 'Style Code', required: false },
        { id: 'el-fit', type: 'select', label: 'Fit', required: false, options: ['Regular', 'Slim', 'Relaxed', 'Oversized'] },
        { id: 'el-season', type: 'select', label: 'Season', required: false, options: ['Spring', 'Summer', 'Autumn', 'Winter', 'All Season'] },
        { id: 'el-fabric', type: 'text', label: 'Fabric', required: false },
        { id: 'el-pattern', type: 'text', label: 'Pattern', required: false },
        { id: 'el-gender', type: 'select', label: 'Gender', required: false, options: ['Men', 'Women', 'Kids', 'Unisex'] },
      ]},
      { id: 'section-3', title: 'Pricing', elements: [
        { id: 'el-mrp', type: 'number', label: 'MRP', required: true },
        { id: 'el-rate', type: 'number', label: 'Rate', required: true },
        { id: 'el-wsp', type: 'number', label: 'WSP', required: false },
      ]},
    ],
  },
  {
    id: 'grocery_fmcg',
    name: 'Grocery / FMCG',
    icon: Apple,
    color: '#15803d',
    description: 'Grocery: Batch tracking, Expiry, Weight, Shelf Life, MFG Date',
    sections: [
      { id: 'section-1', title: 'Product Info', elements: [
        { id: 'el-cat', type: 'text', label: 'Category', required: true },
        { id: 'el-brand', type: 'text', label: 'Brand', required: true },
        { id: 'el-weight', type: 'number', label: 'Net Weight (g)', required: true },
        { id: 'el-uom', type: 'select', label: 'UOM', required: true, options: ['Kg', 'g', 'L', 'ml', 'Pcs', 'Pkt', 'Box'] },
      ]},
      { id: 'section-2', title: 'Batch & Expiry', elements: [
        { id: 'el-batch', type: 'checkbox', label: 'Enable Batch Tracking', required: false },
        { id: 'el-expiry', type: 'checkbox', label: 'Enable Expiry Tracking', required: false },
        { id: 'el-shelflife', type: 'number', label: 'Shelf Life (days)', required: false },
        { id: 'el-mfgdate', type: 'date', label: 'MFG Date', required: false },
        { id: 'el-storage', type: 'select', label: 'Storage Type', required: false, options: ['Room Temp', 'Refrigerated', 'Frozen', 'Cool & Dry'] },
      ]},
      { id: 'section-3', title: 'Pricing', elements: [
        { id: 'el-mrp', type: 'number', label: 'MRP', required: true },
        { id: 'el-rate', type: 'number', label: 'Purchase Rate', required: true },
      ]},
    ],
  },
  {
    id: 'electronics',
    name: 'Electronics',
    icon: Monitor,
    color: '#0369a1',
    description: 'Electronics: Serial No tracking, Warranty, Model, Specs',
    sections: [
      { id: 'section-1', title: 'Product Identity', elements: [
        { id: 'el-cat', type: 'text', label: 'Category', required: true },
        { id: 'el-brand', type: 'text', label: 'Brand', required: true },
        { id: 'el-model', type: 'text', label: 'Model No', required: true },
        { id: 'el-specs', type: 'textarea', label: 'Specifications', required: false },
      ]},
      { id: 'section-2', title: 'Tracking', elements: [
        { id: 'el-serial', type: 'checkbox', label: 'Enable Serial No Tracking', required: false },
        { id: 'el-warranty', type: 'number', label: 'Warranty (months)', required: false },
        { id: 'el-imei', type: 'text', label: 'IMEI / Serial', required: false },
        { id: 'el-voltage', type: 'select', label: 'Voltage', required: false, options: ['110V', '220V', '110-240V', 'Battery'] },
      ]},
      { id: 'section-3', title: 'Pricing', elements: [
        { id: 'el-mrp', type: 'number', label: 'MRP', required: true },
        { id: 'el-rate', type: 'number', label: 'Dealer Price', required: true },
        { id: 'el-wsp', type: 'number', label: 'WSP', required: false },
      ]},
    ],
  },
  {
    id: 'pharmacy',
    name: 'Pharmacy',
    icon: Pill,
    color: '#dc2626',
    description: 'Pharma: Drug Schedule, Composition, Dosage, Batch, Expiry mandatory',
    sections: [
      { id: 'section-1', title: 'Drug Info', elements: [
        { id: 'el-cat', type: 'text', label: 'Drug Category', required: true },
        { id: 'el-comp', type: 'textarea', label: 'Composition / Salt', required: true },
        { id: 'el-dosage', type: 'text', label: 'Dosage Form', required: true },
        { id: 'el-schedule', type: 'select', label: 'Drug Schedule', required: true, options: ['H', 'H1', 'X', 'G', 'J', 'OTC', 'Schedule C', 'Schedule C1'] },
        { id: 'el-pres', type: 'checkbox', label: 'Prescription Required', required: false },
      ]},
      { id: 'section-2', title: 'Batch & Expiry', elements: [
        { id: 'el-batch', type: 'checkbox', label: 'Batch Tracking (Mandatory)', required: false },
        { id: 'el-expiry', type: 'checkbox', label: 'Expiry Tracking (Mandatory)', required: false },
        { id: 'el-mfg', type: 'text', label: 'Manufacturer', required: true },
        { id: 'el-pack', type: 'text', label: 'Pack Size', required: false },
      ]},
      { id: 'section-3', title: 'Pricing', elements: [
        { id: 'el-mrp', type: 'number', label: 'MRP', required: true },
        { id: 'el-rate', type: 'number', label: 'PTR (Price to Retailer)', required: true },
        { id: 'el-pts', type: 'number', label: 'PTS (Price to Stockist)', required: false },
      ]},
    ],
  },
];

/* ═══════════════════════════════════════════
   MAIN SCREEN
═══════════════════════════════════════════ */
const IndustryConfigScreen = ({ theme = 'light', onBack }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConfig, setActiveConfig] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [previewConfig, setPreviewConfig] = useState(null);

  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';

  // Load saved configs
  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/formbuilder/list`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success) setConfigs((j.data || []).filter(f => f.FormName?.startsWith('INDUSTRY_')));
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  // Save config from builder
  const handleFormCreated = async (formConfig) => {
    setShowBuilder(false);
    try {
      const formName = `INDUSTRY_${editingConfig?.id || Date.now()}`;
      const method = editingConfig?.dbId ? 'PUT' : 'POST';
      const url = editingConfig?.dbId ? `${API_URL}/api/formbuilder/${editingConfig.dbId}` : `${API_URL}/api/formbuilder`;
      await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ FormConfig: JSON.stringify(formConfig), FormName: formName }),
      });
      erpToast.success('Industry config saved!');
      loadConfigs();
    } catch { erpToast.error('Save failed'); }
    setEditingConfig(null);
  };

  // Apply template — open builder with pre-loaded sections
  const applyTemplate = (template) => {
    setEditingConfig({ id: template.id, name: template.name });
    setShowBuilder(true);
  };

  // Edit existing config
  const editConfig = async (cfg) => {
    try {
      const r = await fetch(`${API_URL}/api/formbuilder/${cfg.Id}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success && j.data) {
        const parsed = typeof j.data.FormConfig === 'string' ? JSON.parse(j.data.FormConfig) : j.data.FormConfig;
        setEditingConfig({ id: cfg.FormName, name: cfg.FormName, dbId: cfg.Id });
        setPreviewConfig(null);
        setShowBuilder(true);
      }
    } catch { erpToast.error('Failed to load config'); }
  };

  // Preview config
  const previewConfigForm = async (cfg) => {
    try {
      const r = await fetch(`${API_URL}/api/formbuilder/${cfg.Id}`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.success && j.data) {
        const parsed = typeof j.data.FormConfig === 'string' ? JSON.parse(j.data.FormConfig) : j.data.FormConfig;
        setPreviewConfig(parsed);
        setShowBuilder(false);
      }
    } catch { erpToast.error('Failed to load'); }
  };

  // Delete config
  const deleteConfig = async (cfg) => {
    if (!window.confirm(`Delete "${cfg.FormName}"?`)) return;
    try {
      await fetch(`${API_URL}/api/formbuilder/${cfg.Id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      erpToast.success('Deleted');
      loadConfigs();
    } catch { erpToast.error('Delete failed'); }
  };

  // Set as active industry config
  const setAsActive = (cfg) => {
    localStorage.setItem('retailcliq_active_industry', cfg.FormName);
    setActiveConfig(cfg.FormName);
    erpToast.success(`Active config: ${cfg.FormName.replace('INDUSTRY_', '')}`);
  };

  useEffect(() => { setActiveConfig(localStorage.getItem('retailcliq_active_industry') || ''); }, []);

  // Builder view
  if (showBuilder) {
    return (
      <div className="erp-module" data-testid="industry-config-builder">
        <div className="erp-screen-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setShowBuilder(false)} className="erp-btn erp-btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }}><ChevronLeft size={14} /> Back</button>
            <span className="erp-screen-title">Form Builder — {editingConfig?.name || 'New Config'}</span>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <AdvancedFormBuilder onFormCreated={handleFormCreated} initialForm={null} />
        </div>
      </div>
    );
  }

  // Preview view
  if (previewConfig) {
    return (
      <div className="erp-module" data-testid="industry-config-preview">
        <div className="erp-screen-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setPreviewConfig(null)} className="erp-btn erp-btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }}><ChevronLeft size={14} /> Back</button>
            <span className="erp-screen-title">Preview — Industry Config</span>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-4)' }}>
          <AdvancedGeneratedForm formConfig={previewConfig} onSubmit={(data) => { console.log('Preview submit:', data); erpToast.info('Preview mode — data not saved'); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="erp-module" data-testid="industry-config-screen">
      <div className="erp-screen-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onBack && <button onClick={onBack} className="erp-btn erp-btn-ghost" style={{ padding: '3px 8px', fontSize: 11 }} data-testid="screen-back-btn"><ChevronLeft size={14} /> BACK</button>}
          <span className="erp-screen-title">Industry Configuration</span>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--erp-info-light)', color: 'var(--erp-info)' }}>{configs.length} configs</span>
        </div>
        <div className="erp-screen-actions">
          {isAdmin && <button className="erp-btn erp-btn-primary" onClick={() => { setEditingConfig(null); setShowBuilder(true); }} data-testid="new-config-btn"><Plus size={13} /> New Config</button>}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-4)' }}>
        {/* Industry Templates */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--erp-text)' }}>
            <Factory size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Quick Start Templates
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {INDUSTRY_TEMPLATES.map(t => (
              <div key={t.id} className="erp-form-card" style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => isAdmin && applyTemplate(t)}
                onMouseEnter={e => { if (!e.currentTarget) return; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { if (!e.currentTarget) return; e.currentTarget.style.boxShadow = 'var(--erp-shadow)'; }}
                data-testid={`template-${t.id}`}>
                <div style={{ padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `${t.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <t.icon size={16} style={{ color: t.color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--erp-text)' }}>{t.name}</div>
                      <div style={{ fontSize: 9, color: 'var(--erp-text-muted)' }}>{t.sections.reduce((s, sec) => s + sec.elements.length, 0)} fields</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--erp-text-secondary)', lineHeight: 1.4 }}>{t.description}</div>
                  {isAdmin && (
                    <button className="erp-btn erp-btn-ghost" style={{ marginTop: 8, fontSize: 9, padding: '3px 8px' }}>
                      <Copy size={10} /> Use Template
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Saved Configs */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--erp-text)' }}>
            <Settings size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Saved Configurations
          </div>
          <div className="erp-grid-wrapper">
            <table className="erp-grid" style={{ minWidth: 'unset' }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Active</th>
                  <th>Config Name</th>
                  <th>Created</th>
                  <th style={{ width: 180 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--erp-text-muted)' }}>Loading...</td></tr>
                ) : configs.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--erp-text-muted)', fontSize: 11 }}>No configs yet. Use a template above or create new.</td></tr>
                ) : configs.map(cfg => (
                  <tr key={cfg.Id}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="radio" name="activeIndustry" checked={activeConfig === cfg.FormName} onChange={() => setAsActive(cfg)} style={{ width: 14, height: 14, accentColor: 'var(--erp-primary)' }} />
                    </td>
                    <td className="cell-bold" style={{ color: activeConfig === cfg.FormName ? 'var(--erp-primary)' : 'inherit' }}>
                      {cfg.FormName?.replace('INDUSTRY_', '')}
                      {activeConfig === cfg.FormName && <span style={{ fontSize: 9, background: 'var(--erp-success-light)', color: 'var(--erp-success)', padding: '1px 6px', borderRadius: 10, marginLeft: 8 }}>ACTIVE</span>}
                    </td>
                    <td className="cell-muted">{cfg.CreatedAt}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="erp-btn-icon view" onClick={() => previewConfigForm(cfg)} title="Preview"><Eye size={12} /></button>
                        {isAdmin && <button className="erp-btn-icon edit" onClick={() => editConfig(cfg)} title="Edit"><Edit3 size={12} /></button>}
                        {isAdmin && <button className="erp-btn-icon delete" onClick={() => deleteConfig(cfg)} title="Delete"><Trash2 size={12} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryConfigScreen;
